import React from 'react';
import { X, Loader2, QrCode, RefreshCw, AlertTriangle } from 'lucide-react';
import usePosPaymentRequest from './usePosPaymentRequest';

// Scan to pay — the customer pays on their own phone through Square's hosted
// page, so no card is ever typed on the iPad and no hardware is needed. The POS
// watches the Square order and closes the sale the moment the money lands.

export default function WalkInQrPayment({ payload, basketKey, onPaid, onCancel }) {
  const { request, status, error, secondsLeft, cancel, refresh } = usePosPaymentRequest({
    functionName: 'posQrPayment',
    payload,
    basketKey,
    onPaid,
    onCancel,
  });

  const amount = Number(request?.amount ?? payload.amount) || 0;
  const label = request?.label || payload.experienceName || payload.experienceType || 'Venue sale';
  const expired = status === 'expired' || (status === 'pending' && secondsLeft === 0);
  const waiting = status === 'pending' && !expired;
  const countdown = secondsLeft == null
    ? null
    : `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg my-auto">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">SCAN TO PAY</p>
            <p className="text-white font-black text-4xl mt-1">£{amount.toFixed(2)}</p>
            <p className="text-white/40 text-xs mt-1">{label}</p>
          </div>
          <button onClick={cancel} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {status === 'starting' && (
            <p className="flex items-center justify-center gap-2 text-white/40 text-sm py-12">
              <Loader2 className="w-4 h-4 animate-spin" /> Creating the payment code…
            </p>
          )}

          {waiting && request?.qrDataUrl && (
            <>
              <div className="bg-white p-3 sm:p-4 mx-auto w-full max-w-[380px]">
                <img src={request.qrDataUrl} alt="Scan to pay" className="w-full h-auto block" />
              </div>
              <p className="text-center text-white/50 text-sm leading-relaxed">
                Ask the customer to scan this with their phone camera and pay with Apple Pay,
                Google Pay or card.
              </p>
              <p className="flex items-center justify-center gap-2 text-white/40 text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Waiting for payment
                {countdown && <span className="text-white/70 font-bold">· expires in {countdown}</span>}
              </p>
            </>
          )}

          {expired && (
            <div className="border border-amber-500/40 bg-amber-500/10 px-4 py-6 text-center">
              <AlertTriangle className="w-6 h-6 text-amber-300 mx-auto mb-2" />
              <p className="text-amber-300 font-bold text-sm">This code has expired</p>
              <p className="text-amber-300/70 text-xs mt-1">
                It has been withdrawn and can no longer be paid.
              </p>
              <button
                onClick={() => refresh()}
                className="mt-4 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-black text-xs tracking-widest px-6 py-3.5 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> GET A NEW CODE
              </button>
            </div>
          )}

          {status === 'failed' && (
            <div className="border border-red-500/40 bg-red-500/10 px-4 py-4">
              <p className="text-red-400 font-bold text-sm">
                {error || 'The payment could not be started.'}
              </p>
              <button
                onClick={() => refresh()}
                className="mt-3 inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white/70 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" /> TRY AGAIN
              </button>
            </div>
          )}

          {waiting && error && (
            <p className="text-amber-300 text-xs text-center">{error}</p>
          )}

          <button
            onClick={cancel}
            className="w-full border border-white/15 hover:border-white/35 text-white/60 hover:text-white font-bold text-xs tracking-widest py-4 transition-colors"
          >
            CANCEL
          </button>

          <p className="flex items-center justify-center gap-1.5 text-white/30 text-[10px] font-bold tracking-widest">
            <QrCode className="w-3.5 h-3.5" /> SECURED BY SQUARE · APPLE PAY · GOOGLE PAY · CARD
          </p>
        </div>
      </div>
    </div>
  );
}